import React, { useEffect, useMemo, useRef } from 'react'
import { useApiListRoleOrgUser } from '../../api-new/state/content'
import { Card } from '../../shared/card/card'
import { SelectTag } from '../../shared/select-tag/select-tag'
import { useNotificationContext } from '../../context/notification/context'
import { serverMessage } from '../../shared/error/server-message'
import { ErrorReference } from '../../shared/error/error-reference'
import { useErrorLine } from '../../shared/error/use-error-line'
import { withReference } from '../../shared/error/error-notice'
import { useRoleOrgUser } from '../hook/role-org'

export const LAST_OWNER_HINT = 'The last owner. Make someone else an owner first.'

// A member's roles. Auto-user roles (every member always has them; the
// server refuses removing one) are shown locked, marked "Always included".
//
// The owner role follows the content API's owner facts on each row:
// `canChange` (the caller may grant or remove it here) and `lastOwner` (this
// member is the organization's last owner, so it stays). Callers who cannot
// change it only see it on members who hold it. A refused change shows the
// server's message and reloads the roles.
export const RoleSelectUser = ({ userID }) => {
  const apiListRoleOrgUser = useApiListRoleOrgUser()

  const roleOrgUser = useRoleOrgUser()

  const notificationContext = useNotificationContext()

  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const failed = useRef(false)

  // Nobody awaits this: a refusal (404 for a member who was removed) is shown
  // in the card, not left as an uncaught rejection.
  useEffect(
    () => {
      let live = true
      Promise.resolve(apiListRoleOrgUser.get(`${userID}/user`)).then(
        () => { if (live && failed.current) { failed.current = false; setLoadError(null) } },
        err => { if (live) { failed.current = true; setLoadError(serverMessage(err, "This person's roles couldn't be loaded."), err) } }
      )
      return () => { live = false }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userID]
  )

  const visible = useMemo(() => (apiListRoleOrgUser.items || []).filter(item => !item.isAutoOwner || item.isActive || item.canChange), [
    apiListRoleOrgUser.items
  ])

  const change = async fn => {
    try {
      await fn()
    } catch (err) {
      notificationContext.toast.notify(withReference(serverMessage(err, 'Could not change the role'), err), 5000)
    }
    try {
      await apiListRoleOrgUser.get(`${userID}/user`)
    } catch {}
  }

  const handleAdd = item => change(() => apiListRoleOrgUser.post({ roleOrgID: item.roleOrgID, userID: userID }))

  const handleRemove = item => change(() => apiListRoleOrgUser.delete(`${item.roleOrgID}/${userID}`))

  return (
    <React.Fragment>
      <Card title="User Roles">
        {loadError && <p className="text-danger small mb-2" role="alert">{loadError}</p>}
        {loadError && <ErrorReference error={loadFailure} className="mb-2" />}
        <SelectTag
          selectText="select role"
          data={visible}
          enabled={roleOrgUser.accessUpdate}
          fnLabel={item => item.roleName}
          fnActive={item => item.isActive}
          fnReadOnly={item => (item.isAutoOwner ? !item.canChange : item.isReadOnly || item.isAutoUser)}
          fnHint={item => (item.isAutoUser ? 'Always included' : item.isAutoOwner && item.lastOwner ? LAST_OWNER_HINT : '')}
          onAdd={handleAdd}
          onRemove={handleRemove}
        />
      </Card>
    </React.Fragment>
  )
}
